import DVIRLog, { IDefectRemark, IDvirDefects } from "../../model/DVIRLog";
import DefectListTranslation from "../../utils/defectListTranslation";
import { htmlEscape, createElement } from "../../utils/vanilla";
import Translator from "../../utils/translator";
import { getLabel } from "./helper";
import { DriveStore } from "../../store/reducers";
import { openPictureOptionDialog, getCameraIcon } from "./imageHelper";
import { isCameraPluginPresent } from "../../utils/native/nativeFileApi";
import { UserService } from "../../utils/users";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { renderCheckbox } from "../framework/checkbox";
import { UserHosRuleSetService } from "../../model/UserHosRuleSets";
import { SecurityService } from "../../utils/security";
import { IDefect, TDefectSeverity } from "../../model/Defect";

interface IInspectionListOptions {
    language?: string;
    translator: Translator;
    model: DVIRLog;
    viewOnly?: boolean;
    onChange?: () => void;
    defectList: IDefect;
    store: DriveStore;
    listenTo?: any;
}

interface IInspectionListItem {
    id: string;
    name: string;
    severity: TDefectSeverity;
    children: any[];
    parentId?: string;
    noTranslate: boolean;
    isSelectablePart: boolean;
    isHidden: boolean;
    isRequired: boolean;
    isParent: boolean;
}

export default class InspectionChecklist {
    private readonly language?: string;
    private readonly defectListTranslation: DefectListTranslation;
    private readonly translator: Translator;
    private readonly currentUser: DeepReadonly<IUser> | null;
    private readonly parentCallback?: () => void;
    private readonly viewOnly?: boolean;
    private readonly model: DVIRLog;
    private readonly defectList: IDefect;
    private readonly store: DriveStore;
    private readonly showCameraIcons: boolean;
    private readonly canUseMedia: boolean;
    private readonly canViewMedia: boolean;
    private readonly defects?: IDvirDefects[];


    private parentListContainer: any;
    private eventCallback: (e: Event) => void;
    private partsHash: IIdDictionary<string[]>;
    private translatedStrings: { [key: string]: string };
    private toggleDefectButtons: HTMLButtonElement[] = [];

    private readonly strings: string[] = [
        "{0} defects",
        "{0} defect",
        "Add Defect",
        "Add defect {0}",
        "Remove",
        "Remove defect {0}",
        "Existing defect",
        "Defective",
        "Add a remark",
        "Add a remark for {0}",
        "Defects dropdown for {0}",
        "{0} error. Missing required remark."
    ];

    constructor (
        options: IInspectionListOptions,
        private readonly userService: UserService,
        readonly userHosRuleSetService: UserHosRuleSetService,
        securityService: SecurityService
    ) {
        this.currentUser = userService.getCurrentUser();
        this.language = options.language;
        this.translator = options.translator;
        this.model = options.model || new DVIRLog();
        this.defectListTranslation = new DefectListTranslation(this.translator, this.language);
        this.parentCallback = options.onChange;
        this.viewOnly = options.viewOnly;
        this.defectList = options.defectList;
        this.store = options.store;

        this.showCameraIcons = this.checkShowCameraIcons();
        this.canViewMedia = securityService.can("viewMedia", this.currentUser?.id);
        this.canUseMedia = this.canViewMedia && securityService.can("manageMedia", this.currentUser?.id);
        this.defects = this.viewOnly ? this.model.dVIRDefects : this.model.getCarryOverDefects();
    }

    translateDefectName(defectName: string) {
        return this.defectListTranslation.translate(defectName);
    }

    renderList (parentElement?: Element, force?: boolean) {
        return this.translator.translate(this.strings, this.language)
            .then(translatedStrings => {
                this.translatedStrings = {};
                translatedStrings.forEach((dvirString, index) => {
                    this.translatedStrings[this.strings[index]] = dvirString;
                });

                return (this.parentListContainer && !force ? Promise.resolve() : this.createChecklist());
            })
            .then(() => this.viewOnly ? Promise.resolve(this.parentListContainer) : this.translator.translateHtml([this.parentListContainer], this.language))
            .then(translatedElement => {
                let listHtml = Array.isArray(translatedElement) ? translatedElement[0] : translatedElement;
                if (parentElement) {
                    parentElement.innerHTML = "";
                    parentElement.appendChild(listHtml);
                }

                return listHtml;
            });
    }

    remove () {
        if (this.toggleDefectButtons) {
            for (let i = 0; i < this.toggleDefectButtons.length; i++) {
                let btn = this.toggleDefectButtons[i];
                btn.removeEventListener("click", this.eventCallback);
            }
        }

        this.parentListContainer && this.parentListContainer.remove();
        this.parentListContainer = null;
    }

    saveRemarks (repairDate: string) {
        this.hideErrorBars();
        let missingRequiredRemarks = false;
        let defects = this.model.getClonedAllDefects();
        defects.forEach(dvirDefect => {
            let isPart = !!this.partsHash[dvirDefect.defect.id];
            let defectId = dvirDefect.defect.id;
            let inputElement = this.parentListContainer.querySelector(`input[data-id="${ defectId }"].new-defect-remark`) as HTMLInputElement;

            let remarks: IDefectRemark[] = dvirDefect.defectRemarks || [];
            if (inputElement && inputElement.value !== "") {
                remarks.push({
                    remark: inputElement.value,
                    dateTime: repairDate,
                    user: this.currentUser ? { id: this.currentUser.id } : undefined
                });

                dvirDefect.defectRemarks = remarks;
            }

            if (isPart && remarks.length === 0) {
                this.showErrorBar(dvirDefect.defect.id);
                missingRequiredRemarks = true;
            }


        });

        if (missingRequiredRemarks) {
            throw new Error("Missing required remarks");
        }

        return defects;
    }

    show(element: HTMLElement) {
        element.style.display = "block";
    }

    showVisibility(element: HTMLElement) {
        element.classList.remove("visually-hidden");
    }

    hide(element: HTMLElement) {
        element.style.display = "none";
    }

    hideVisibility(element: HTMLElement) {
        element.classList.add("visually-hidden");
    }

    validateRequiredParts() {
        let noDefectCheckboxes = document.querySelectorAll(".inspectionList-checkbox .drive-checkbox__input");
        for (let i = 0; i < noDefectCheckboxes.length; i++) {
            let noDefectCheckbox = noDefectCheckboxes[i] as HTMLInputElement;
            if (noDefectCheckbox.parentElement && noDefectCheckbox.parentElement.style && noDefectCheckbox.parentElement.style.display !== "none" && !noDefectCheckbox.checked) {
                noDefectCheckbox && (noDefectCheckbox.disabled = false);
                let nextCheckId = noDefectCheckbox.id.replace("checkbox_", "");
                let partInput = document.querySelector(`.defect-parent-item input#${ nextCheckId }`) as HTMLInputElement;
                if (partInput) {
                    partInput.checked = true;
                    partInput.parentElement && partInput.parentElement.scrollIntoView();
                    return true;
                }
            }
        }
        return false;
    }

    checkShowCameraIcons() {
        return window.driveApp?.debug?.base64TestImage || isCameraPluginPresent();
    }

    private hideErrorBars () {
        let errorMessageElements: NodeListOf<HTMLElement> = document.querySelectorAll("div.remark-required-warning");
        for (let i = 0; i < errorMessageElements.length; i++) {
            let errorBox = errorMessageElements[i];
            errorBox && this.hide(errorBox);
        }
    }

    private showErrorBar(defectId: string) {
        let partInputElement = document.querySelector(`input#${ defectId }`);
        if (partInputElement) {
            (partInputElement as HTMLInputElement).checked = true;
        }

        let errorMessageElement = document.querySelector(`div#remark-required-warning_${ defectId }`);
        errorMessageElement && this.show(errorMessageElement as HTMLElement);
    }

    private createChecklist(): Promise<void> {
        return new Promise(resolve => {
            if (this.parentListContainer) {
                this.parentListContainer.remove();
            }

            this.parentListContainer = createElement("div", undefined, "dvir-inspection-list");
            this.defectListTranslation.ready(() => {
                this.createNewList();
                resolve();
            });
        });
    }

    private createNewList () {
        let defectGroup = this.defectList;

        if (!defectGroup) {
            return;
        }

        let [requiredDefectsArray, nonRequiredDefectsArray] = this.sortPartsAndGetHash(defectGroup.children);

        if (!this.viewOnly) {
            this.eventCallback = this.selectDefects.bind(this);
        }

        if (requiredDefectsArray.length) {
            let requiredTitleContainer = createElement("span", "title-line");
            requiredTitleContainer.innerHTML = `<span>Required checklist</span>`;
            this.parentListContainer.appendChild(requiredTitleContainer);

            this.createList(requiredDefectsArray, true);

            if (nonRequiredDefectsArray.length) {
                let optionalTitleContainer = createElement("span", "title-line");
                optionalTitleContainer.innerHTML = `<span>Other parts (optional)</span>`;
                this.parentListContainer.appendChild(optionalTitleContainer);
                this.createList(nonRequiredDefectsArray, false);
            }
        } else {
            this.createList(nonRequiredDefectsArray, false);
        }

        this.populateSelectedDefects();
        this.disableNoDefectChecks();
        this.addInspectEventListeners();
        this.openNext();
    }

    private createList(parts: IDefect[], isRequired: boolean) {
        let parentList = createElement("ul", "dvir-parent-list", isRequired ? "required-defects-list" : "defects-list");
        let partItems = this.createChecklistItems(parts);
        partItems.forEach(partItem => {
            let parentLi = this.createLi(partItem, true);
            let parentLabel = this.createHeading(partItem, true);
            parentLi.appendChild(parentLabel);

            let hiddenDiv = createElement("div", "hidden-div", `hidden_div_${ parentLi.id }`);
            hiddenDiv.appendChild(this.createNestedList(this.sortDefects(partItem.children)[1], partItem.id, partItem.isHidden && !this.isDefectSelected(partItem.id)));
            parentLi.appendChild(hiddenDiv);

            parentList.appendChild(parentLi);
            this.parentListContainer.appendChild(parentList);
        });
    }

    private createLi (listItem: IInspectionListItem, isParent?: boolean): HTMLLIElement {
        let listElement = createElement("li", isParent ? "defect-parent-item" : "defect-child-item", `${ isParent ? "parent" : "child" }_${ listItem.id }`);

        if (isParent) {
            let inputElement = createElement("input", "defect-container-checkbox", listItem.id);
            inputElement.setAttribute("type", "checkbox");
            inputElement.setAttribute("aria-controls", `hidden_div_${ listElement.id }`);
            inputElement.setAttribute("aria-expanded", "false");
            inputElement.addEventListener("change", (event) => {
                const target = event.target as HTMLInputElement;
                target.setAttribute("aria-expanded", `${ target.checked }`);
            });
            listElement.appendChild(inputElement);
        } else {
            listItem.parentId && listElement.setAttribute("data-parent-id", listItem.parentId);
        }

        return listElement;
    }

    private createHeading (listItem: IInspectionListItem, isParent?: boolean): HTMLElement {
        let container = createElement("div", isParent ? "parent-heading" : "child-heading");

        if (isParent) {
            let labelElement = createElement("label");
            labelElement.setAttribute("for", listItem.id);
            labelElement.innerHTML = `<svg class='svgIcon tabIndex="-1" expandIcon bottom-margin' aria-hidden="true"><use xlink:href='#drive-arrow-right'></use></svg>
                                      <svg class='svgIcon tabIndex="-1" collapseIcon bottom-margin' aria-hidden="true"><use xlink:href='#drive-arrow-down'></use></svg>`;
            this.createAndAppendPartTitle(listItem, labelElement, isParent);
            container.appendChild(labelElement);

            !this.viewOnly && listItem.isRequired && container.appendChild(renderCheckbox({
                customClass: "inspectionList-checkbox",
                title: "Mark part as having no defects",
                labelText: "No defects",
                id: listItem.id,
                isRequired: true,
                isReversed: true
            }));
            container.appendChild(this.createAlertTag());
        } else {
            this.createAndAppendPartTitle(listItem, container, isParent);
        }

        return container;
    }

    private createAndAppendPartTitle (defectItem: IInspectionListItem, parentElement: Element, isParent?: boolean) {
        let titleSpanElement = createElement("span", "list-title");

        let severity = defectItem.severity.toLowerCase();
        let defectDisplayName = !defectItem.noTranslate ? defectItem.name : this.defectListTranslation.translate(defectItem.name);
        let defectLabel = defectDisplayName;
        let subtitleDivElement = createElement("div", `list-subtitle`);
        subtitleDivElement.innerHTML = `<span class="defect-name-container${ isParent ? " part-name" : "" } ${ severity }">
                                            <span tabIndex="-1" class="defect-name${ defectItem.noTranslate ? " noTranslate" : "" }" aria-label="${ defectLabel }" data-ariaTranslated="true">${ htmlEscape(defectDisplayName) }</span>
                                            ${ getLabel(severity) }
                                        </span>`;

        titleSpanElement.appendChild(subtitleDivElement);

        let hiddenTextInputElement: HTMLDivElement | undefined = undefined;
        if (!defectItem.isParent) {
            let addDefectButton = createElement("button", "button button_simple toggle-defect-button noTranslate");
            addDefectButton.setAttribute("data-severity", defectItem.severity);
            addDefectButton.setAttribute("data-id", defectItem.id);
            defectItem.parentId && addDefectButton.setAttribute("data-parent-id", defectItem.parentId);
            addDefectButton.textContent = this.translatedStrings["Add Defect"];
            let addDefectAriaLabel = this.translatedStrings["Add Defect"];
            addDefectButton.setAttribute("aria-label", addDefectAriaLabel);
            addDefectButton.setAttribute("data-ariaTranslated", "true");
            titleSpanElement.appendChild(addDefectButton);

            if (!this.viewOnly) {
                hiddenTextInputElement = createElement("div", "hidden-text-input");
                this.hide(hiddenTextInputElement);

                // We only want this error message element for 'parts'
                if (defectItem.isSelectablePart) {
                    let inputErrorElement = createElement("div", "noticeBox noticeBox--type-error remark-required-warning", `remark-required-warning_${ defectItem.id }`);
                    this.hide(inputErrorElement);
                    let errorAriaLabel = this.translatedStrings["Add a remark"];
                    inputErrorElement.setAttribute("tabindex", "0");
                    inputErrorElement.setAttribute("aria-label", errorAriaLabel);
                    inputErrorElement.setAttribute("data-ariaTranslated", "true");
                    inputErrorElement.innerHTML = `<div class="noticeBox__container">
                                                        <span class="noticeBox__title"><svg class="svgIcon big" aria-hidden=true><use xlink:href="#drive-attention"></use></svg></span>
                                                        <span class="noticeBox__content">Add a remark</span>
                                                    </div>`;
                    hiddenTextInputElement.appendChild(inputErrorElement);
                }
                let inputTextAccessibilityLabel = this.translatedStrings["Add a remark"];

                let defectRemarkSpan = createElement("span", "defectRemark");
                let inputTextElement = createElement("input", "new-defect-remark");
                inputTextElement.setAttribute("data-id", defectItem.id);
                inputTextElement.setAttribute("type", "text");
                inputTextElement.setAttribute("maxlength", "500");
                inputTextElement.setAttribute("placeholder", this.translatedStrings["Add a remark"]);
                inputTextElement.setAttribute("aria-label", inputTextAccessibilityLabel);
                inputTextElement.setAttribute("data-ariaTranslated", "true");
                defectRemarkSpan.appendChild(inputTextElement);
                hiddenTextInputElement.appendChild(defectRemarkSpan);

                if (this.showCameraIcons && this.canViewMedia) {
                    let unrepairedDefects = this.model ? this.model.getCarryOverDefects() : [];

                    let carryForwardDefect: IDvirDefects | undefined;
                    unrepairedDefects.some((defect, index)  => {
                        let defectId = defect.defect.id;
                        if (defectId === defectItem.id || defectItem.id.indexOf(defectId) > -1) {
                            carryForwardDefect = unrepairedDefects[index];
                            return true;
                        }
                        return false;
                    });

                    let spanCamera: HTMLElement = createElement("span");
                    if (carryForwardDefect?.defectRemarks && carryForwardDefect.defectRemarks.length > 0) {
                        spanCamera.className = "inspectionListImage";
                        spanCamera.id = `carryForwardDefectRemarkMediaId_${ carryForwardDefect.defectRemarks[0].id }`;
                    } else if (this.canUseMedia) {
                        let defectItemId = defectItem.id;
                        let cameraIconId = `${ defectItemId }_camera`;
                        spanCamera.className = "cameraInspectIcon";
                        spanCamera.id = cameraIconId;
                        spanCamera.innerHTML = getCameraIcon();
                        spanCamera.onclick = e => openPictureOptionDialog(e, this.store, cameraIconId, this.translator, this.model.getAssetType() === "Trailer");
                    }
                    hiddenTextInputElement.appendChild(spanCamera);
                }
            }
        }

        parentElement.appendChild(titleSpanElement);
        hiddenTextInputElement && parentElement.appendChild(hiddenTextInputElement);
    }

    private createAlertTag() {
        let spanAlertTag = createElement("span", "alert-tag defect-count-alert noTranslate");
        spanAlertTag.setAttribute("aria-live", "polite");
        this.hideVisibility(spanAlertTag);
        return spanAlertTag;
    }

    private createNestedList(defects: IDefect[], parentId?: string, isHidden?: boolean) {
        let defectsList = createElement("ul", "dvir-child-list");
        let defectItems = this.createChecklistItems(defects, parentId, isHidden);
        defectItems.forEach(defectItem => {
            let childLi = this.createLi(defectItem);
            let childLabel = this.createHeading(defectItem, false);

            childLi.appendChild(childLabel);
            defectsList.appendChild(childLi);
        });

        return defectsList;
    }

    private sortPartsAndGetHash(parts: IDefect[]) {
        let sortedParts = this.sortDefects(parts);
        this.partsHash = sortedParts[0].concat(sortedParts[1]).reduce((hash: IIdDictionary<string[]>, part) => {
            hash[part.id] = [];
            return hash;
        }, {});
        return sortedParts;
    }

    private sortDefects (defects: IDefect[]) {
        let currentUser = this.userService.getCurrentUser();
        let listNumberRegex = new RegExp(/^([\d|\.]+)/);
        let allNonNumberedParts: any = [];
        let allNumberedParts = (defects || []).filter((a: any) => {
            if (a.name) {
                let aNumberMatch = listNumberRegex.exec(a.name) || null;
                let float = aNumberMatch && parseFloat(aNumberMatch[1]);

                if (float !== null && !isNaN(float)) {
                    a.number = float;
                    return true;
                }
            }

            allNonNumberedParts.push(a);
            return false;
        });
        let sortForNum = (a: any, b: any) => {
            if (a.number < b.number) {
                return -1;
            }

            if (a.number > b.number) {
                return 1;
            }

            return 0;
        };
        let sortForLetters = (a: any, b: any) => {
            if (a.name && a.name.toLowerCase() === "other") {
                return 1;
            }

            if (a.name && b.name) {
                return a.name.localeCompare(b.name, currentUser ? currentUser.language : undefined);
            }

            return 0;
        };

        let sortedDefectsArray: IDefect[] = allNumberedParts.sort(sortForNum).concat(allNonNumberedParts.sort(sortForLetters));
        let requiredDefectsArray: IDefect[] = [];
        let nonRequiredDefectsArray: IDefect[] = [];

        sortedDefectsArray.forEach(defect => {
            if (defect.isRequired) {
                requiredDefectsArray.push(defect);
            } else {
                nonRequiredDefectsArray.push(defect);
            }
        });

        return [requiredDefectsArray, nonRequiredDefectsArray];
    }

    private addInspectEventListeners() {
        this.toggleDefectButtons = this.parentListContainer.querySelectorAll(".toggle-defect-button") || [];

        for (let i = 0; i < this.toggleDefectButtons.length; i++) {
            let btn = this.toggleDefectButtons[i];
            btn.addEventListener("click", this.eventCallback);
        }
    }

    private createChecklistItems (defects: IDefect[], parentId?: string, parentIsHidden?: boolean): IInspectionListItem[] {
        let checklistItems = defects.map(defect => {
            return {
                id: defect.id,
                name: defect.name,
                severity: defect.severity || "Normal",
                parentId,
                children: defect.children || [],
                noTranslate: true,
                isSelectablePart: false,
                isHidden: defect.isHidden || false,
                isParent: !parentId,
                isRequired: defect.isRequired || false
            };
        });

        if (parentId && !parentIsHidden) {
            checklistItems.push({
                id: parentId,
                name: "Other",
                severity: "Normal",
                parentId,
                children: [],
                noTranslate: true,
                isSelectablePart: true,
                isHidden: false,
                isParent: false,
                isRequired: false
            });
        }

        return checklistItems;
    }

    private disableNoDefectChecks() {
        let noDefectCheckboxes = this.parentListContainer.querySelectorAll(".inspectionList-checkbox");
        for (let i = 0; i < noDefectCheckboxes.length; i++) {
            let noDefectCheckbox = noDefectCheckboxes[i] as HTMLElement;
            if (noDefectCheckbox.style && noDefectCheckbox.style.display !== "none") {
                let input = noDefectCheckbox.querySelector(".drive-checkbox__input") as HTMLInputElement;

                if (input) {
                    input.addEventListener("change", this.openNext.bind(this), false);
                    input.disabled = true;
                    let id = input.id.replace("checkbox_", "");
                    let partInput = this.parentListContainer.querySelector(`.defect-container-checkbox#${ id }`);

                    if (partInput) {
                        partInput.addEventListener("change", this.unfreezeNoDefects.bind(this), false);
                    }
                }
            }
        }
    }

    private unfreezeNoDefects(e) {
        let id = e && e.target && e.target.id;
        if (e && e.target && e.target.checked && id) {
            let noDefectCheckbox = document.querySelector(`#checkbox_${ id }`) as HTMLInputElement;
            noDefectCheckbox && (noDefectCheckbox.disabled = false);
        }
    }

    private openNext(e?) {
        let listContainer = this.parentListContainer || document;
        if ((e && e.target && e.target.checked)) {
            let id = (e && e.target && e.target.id || "").replace("checkbox_", "");
            let container = id && listContainer.querySelector(`.defect-container-checkbox#${ id }`) as HTMLInputElement;
            if (container) {
                container.checked = false;
            }
        }

        let noDefectCheckboxes = listContainer.querySelectorAll(".inspectionList-checkbox .drive-checkbox__input");
        for (let i = 0; i < noDefectCheckboxes.length; i++) {
            let noDefectCheckbox = noDefectCheckboxes[i] as HTMLInputElement;
            if (noDefectCheckbox.parentElement && noDefectCheckbox.parentElement.style && noDefectCheckbox.parentElement.style.display !== "none" && !noDefectCheckbox.checked) {
                let nextCheckId = noDefectCheckbox.id.replace("checkbox_", "");
                let partInput = listContainer.querySelector(`.defect-parent-item input#${ nextCheckId }`) as HTMLInputElement;
                if (partInput) {
                    partInput.checked = true;
                    partInput.parentElement && partInput.parentElement.scrollIntoView();
                }

                let nextNoDefectCheckbox = listContainer.querySelector(`#checkbox_${ nextCheckId }`) as HTMLInputElement;
                if (nextNoDefectCheckbox) {
                    nextNoDefectCheckbox.disabled = false;
                }
                break;
            }
        }
    }

    private populateSelectedDefects () {
        let defects = this.defects;
        if (!defects || defects.length === 0) {
            return;
        }

        let textString = this.viewOnly ? this.translatedStrings["Defective"] : this.translatedStrings["Existing defect"];

        defects.forEach(defect => {
            let defectId = defect.defect.id;
            let defectContainer = this.parentListContainer.querySelector(`li#child_${ defectId }`) as HTMLInputElement;
            if (defectContainer) {
                let partId = defectContainer.getAttribute("data-parent-id");
                if (partId) {
                    this.partsHash[partId].push(defectId);
                    this.onSelected(true, partId, defectId);

                    // this defect can't be removed
                    if (defect.id) {
                        let selectDefectButton = defectContainer.querySelector(".toggle-defect-button") as HTMLButtonElement;
                        if (selectDefectButton) {
                            selectDefectButton.disabled = true;
                            selectDefectButton.textContent = `(${ textString })`;
                            selectDefectButton.style.display = "block";
                        }
                    }
                }
            }
        });
    }

    private isDefectSelected (id: string) { return (this.model.dVIRDefects || []).some(def => def.defect.id === id); }

    private selectDefects(e: Event) {
        dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);

        let target = e.target as HTMLInputElement;
        let partId = target.getAttribute("data-parent-id") as string;
        let severity = target.getAttribute("data-severity") as TDefectSeverity;
        let defectId = target.getAttribute("data-id") as string;
        let isAdding = !this.isDefectSelected(defectId);
        if (isAdding) {
            this.selectDefect(defectId, severity);
            partId && this.partsHash[partId].push(defectId);
        } else {
            this.deselectDefect(defectId);
            partId && (this.partsHash[partId] = this.partsHash[partId].filter(id => id !== defectId));
        }

        this.onSelected(isAdding, partId, defectId);
    }

    private onSelected(isAdding: boolean, partId: string, defectId: string) {
        if (partId) {
            let selectedDefectsForPartCount = this.partsHash[partId].length;
            let partContainer = this.parentListContainer.querySelector(`#parent_${ partId }`);
            if (partContainer) {
                let noDefectsCheckBox = partContainer.querySelector(".inspectionList-checkbox") as HTMLElement;
                let defectCounter = partContainer.querySelector(".defect-count-alert") as HTMLElement;
                if (selectedDefectsForPartCount > 0) {
                    noDefectsCheckBox && this.hide(noDefectsCheckBox);
                    defectCounter && this.showVisibility(defectCounter);
                } else {
                    noDefectsCheckBox && this.show(noDefectsCheckBox);
                    defectCounter && this.hideVisibility(defectCounter);
                }
                let defectString = selectedDefectsForPartCount === 1 ? this.translatedStrings["{0} defect"] : this.translatedStrings["{0} defects"];
                if (defectCounter) {
                    defectCounter.textContent = defectString.replace("{0}", `${ selectedDefectsForPartCount }`);
                }

                let defectContainer = partContainer.querySelector(`#child_${ defectId }`) as HTMLElement;
                if (defectContainer) {
                    let remarkContainer = defectContainer.querySelector(".hidden-text-input") as HTMLElement;
                    let selectDefectButton = defectContainer.querySelector(".toggle-defect-button") as HTMLElement;
                    if (isAdding) {
                        remarkContainer && this.show(remarkContainer);
                        if (selectDefectButton) {
                            selectDefectButton.classList.add("button_simple-negative");
                            selectDefectButton.textContent = this.translatedStrings["Remove"];
                            let newSelectDefectAriaLabel = this.translatedStrings["Remove"];
                            selectDefectButton.setAttribute("aria-label", newSelectDefectAriaLabel);
                        }
                    } else {
                        remarkContainer && this.hide(remarkContainer);
                        if (selectDefectButton) {
                            selectDefectButton.classList.remove("button_simple-negative");
                            selectDefectButton.textContent = this.translatedStrings["Add Defect"];
                            let newSelectDefectAriaLabel = this.translatedStrings["Add Defect"];
                            selectDefectButton.setAttribute("aria-label", newSelectDefectAriaLabel);
                        }
                    }
                }
            }
        }

        this.parentCallback && this.parentCallback();
    }

    private deselectDefect(id: string) {
        let newDefectsList = this.model.dVIRDefects ? this.model.dVIRDefects.filter(def => def.defect.id !== id) : [];
        this.model.dVIRDefects = newDefectsList;
    }

    private selectDefect (id: string, severity: TDefectSeverity = "Normal") {
        let defects = this.model.dVIRDefects || [];
        defects.push({
            defectRemarks: [],
            defect: { id, severity },
            repairStatus: "NotRepaired"
        });
        this.model.dVIRDefects = defects;
    }
}